import { Currency } from '@/components/form/fields/text/helpers/currency';
import { BulkBasicBetterBest } from '@/pages/product/product/bulk/helpers/types';
import { BulkFinanceModelTypes } from '@/pages/settings/bulk/helpers/utils';
import { Product } from '@/services/api/contracts/catalog/types';
import { z } from 'zod';

export const IncomingProductSpec = z.object({
  type: z.string().optional(),
  value: z.string()
});

export type IncomingProductSpec = z.infer<typeof IncomingProductSpec>;

export const IncomingProduct = z.object({
  Name: z.string().nonempty(),
  ManufacturerSKU: z.string().nonempty(),
  Manufacturer: z.string().nonempty(),
  Category: z.string().nonempty(),
  Description: z.string().optional(),
  Price: Currency,
  Margin: z.coerce.number(),
  Replacements: z.string().optional(),
  Specs: z.array(IncomingProductSpec),
  // Zod doesnt handle optional enums correctly.
  // Workaround from https://github.com/react-hook-form/react-hook-form/issues/3985
  BasicBetterBest: z.nativeEnum(BulkBasicBetterBest).or(z.literal('')).optional(),
  Region: z.string()
});

export type IncomingProduct = z.infer<typeof IncomingProduct>;

const parseSpecifications = (record: Record<string, string>) => {
  const Specs = Object.entries(record)
    .filter(([key]) => key.startsWith('Spec'))
    .filter(([, value]) => !!value)
    .map(([key, value]) => {
      const [, type] = key.split(':');
      return { type, value };
    });

  return { ...record, Specs };
};

export const IncomingProductPipeline = IncomingProduct.omit({ Specs: true })
  .catchall(z.string())
  .transform(parseSpecifications)
  .pipe(IncomingProduct);

export const IncomingCustomer = z.object({
  RegName: z.string().nonempty(),
  RegAddress1: z.string().nonempty(),
  RegAddress2: z.string(),
  RegAddressCity: z.string().nonempty(),
  RegAddressCounty: z.string().nonempty(),
  RegPostCode: z.string().nonempty(),
  RegCountry: z.string().nonempty(),
  RegNumber: z.string().nonempty(),
  NumEmployees: z.string().nonempty(),
  RegVatNumber: z.string().nonempty()
});

export type IncomingCustomer = z.infer<typeof IncomingCustomer>;

const parseEmployeeCount = (record: Record<string, string>) => {
  const employeeCounts = Object.entries(record)
    .filter(([key]) => key.startsWith('NumEmployees'))
    .filter(([, value]) => !!value)
    .map(([, value]) => parseInt(value, 10));

  return { ...record, employeeCounts };
};

export const IncomingCustomerPipeline = IncomingCustomer.catchall(z.string())
  .transform(parseEmployeeCount)
  .pipe(IncomingCustomer);

export const Replacement = z.object({
  Product: Product,
  Replaces: z.array(Product)
});

export type Replacement = z.infer<typeof Replacement>;

export const IncomingFinanceModel = z.object({
  Manufacturer: z.string(),
  Category: z.string().nonempty(),
  Type: z.nativeEnum(BulkFinanceModelTypes),
  Term: z.string(),
  Pcms: z.array(
    z.object({
      type: z.string(),
      value: z.coerce.number().default(0)
    })
  )
});

export type IncomingFinanceModel = z.infer<typeof IncomingFinanceModel>;

const parsePcms = (record: Record<string, string>) => {
  const Pcms = Object.entries(record)
    .filter(([key]) => key.startsWith('PCM'))
    .map(([key, value]) => {
      const [, type] = key.split(':');
      return { type, value: Number(value as string) };
    });

  return { ...record, Pcms };
};

export const IncomingFinanceModelPipeline = IncomingFinanceModel.omit({ Pcms: true })
  .catchall(z.string())
  .transform(parsePcms)
  .pipe(IncomingFinanceModel);

const nullInterceptor = (val: unknown) => {
  if (typeof val === 'string' && (val.trim() === 'None' || val.trim() === 'N/A')) {
    return undefined;
  }
  return val;
};

const reverseServiceRequestMap: Record<string, string> = {
  'Deploy': 'DeployInventory',
  'Return': 'OffboardUser',
  'Refresh': 'TechRefresh',
  'ADR': 'AdvDeviceReplacement'
};

const mapServiceRequestType = (val: string) => {
  const trimmed = val.trim();
  return reverseServiceRequestMap[trimmed] || trimmed;
};

export const IncomingRateCardPipeline = z.object({
  Name: z.string().nonempty(),
  'Product ID': z.string().optional(),
  Region: z.string().optional(),
  Price: z.preprocess((val) => {
    const intercepted = nullInterceptor(val);
    if (intercepted === undefined) return undefined;
    let priceVal: number | undefined;
    if (typeof intercepted === 'string') {
      const parsed = parseFloat(intercepted);
      if (!isNaN(parsed)) priceVal = Math.round(parsed * 100);
    }
    else if (typeof intercepted === 'number') {
      priceVal = Math.round(intercepted * 100);
    }
    
    if (priceVal !== undefined) {
      return {
        price: priceVal,
        effectiveDate: { value: new Date().toISOString() }
      };
    }
    
    return intercepted;
  }, z.object({ price: z.number(), effectiveDate: z.object({ value: z.string() }).optional() }).optional()),
  'Applicable Request Types': z.preprocess((val) => {
    const intercepted = nullInterceptor(val);
    if (intercepted === undefined) return [];
    if (typeof intercepted === 'string') {
      return intercepted.split('|').map(s => mapServiceRequestType(s)).filter(Boolean);
    }
    return intercepted;
  }, z.array(z.string()).optional()),
  'Base Pack For': z.preprocess((val) => {
    const intercepted = nullInterceptor(val);
    if (typeof intercepted === 'string') return mapServiceRequestType(intercepted);
    return intercepted;
  }, z.string().optional()),
  'Item Rate For': z.preprocess((val) => {
    const intercepted = nullInterceptor(val);
    if (typeof intercepted === 'string') return mapServiceRequestType(intercepted);
    return intercepted;
  }, z.string().optional()),
  'Max Chargeable Weight (kg)': z.preprocess(nullInterceptor, z.coerce.number().optional()),
  'Is Item Rate Override': z.preprocess((val) => {
    if (typeof val === 'string') return val.trim().toLowerCase() === 'yes';
    return val;
  }, z.boolean().optional()),
  'Is Exclusive Crate': z.preprocess((val) => {
    if (typeof val === 'string') return val.trim().toLowerCase() === 'yes';
    return val;
  }, z.boolean().optional()),
  'Add-On Category': z.preprocess(nullInterceptor, z.string().optional()),
  'Covered By Warranty': z.preprocess((val) => {
    if (typeof val === 'string') return val.trim().toLowerCase() === 'yes';
    return val;
  }, z.boolean().optional())
});

export type IncomingRateCard = z.infer<typeof IncomingRateCardPipeline>;
